import { useEffect, useState } from "react";
import API from "../api/axiosConfig";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Trash2 } from "lucide-react";
import MobileBottomBar from "../components/MobileBottomBar";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

dayjs.extend(relativeTime);


// Map backend actionUrl to the correct frontend route based on user role
function resolveRoute(actionUrl, role) {
  if (!actionUrl) return null;

  // /orders/:id  →  company order detail  |  user orders list  |  ryder dashboard
  const orderMatch = actionUrl.match(/^\/orders\/([a-f0-9-]+)$/i);
  if (orderMatch) {
    if (role === "company") return `/order/${orderMatch[1]}`;
    if (role === "ryder")   return `/ryder-dashboard?tab=asignados`;
    return `/my-orders`;
  }

  // pass through any other internal path (/ryder-dashboard?tab=..., etc.)
  return actionUrl;
}

export default function NotificationsInbox() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  })();

  useEffect(() => {
    API.get("/notifications")
      .then((res) => setItems(res.data?.data?.data ?? res.data?.data ?? []))
      .finally(() => setLoading(false));
  }, []);

  const open = async (n) => {
    if (!n.read) {
      await API.patch(`/notifications/${n.id}/read`).catch(() => {});
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
    }
    const route = resolveRoute(n.actionUrl, user?.role);
    if (route) navigate(route);
  };

  const markAllRead = async () => {
    await API.patch("/notifications/read-all").catch(() => {});
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
  };

  const deleteNotif = async (e, id) => {
    e.stopPropagation();
    await API.delete(`/notifications/${id}`).catch(() => {});
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  const unreadCount = items.filter((n) => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-black max-w-2xl mx-auto p-6 flex justify-center mt-20">
        <div className="animate-spin w-8 h-8 border-4 border-supernova-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black max-w-2xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Bell size={22} className="text-supernova-500" />
          <h1 className="text-xl font-bold text-white">Notificaciones</h1>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>

        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-1.5 text-sm text-supernova-500 hover:text-supernova-400 font-medium"
          >
            <CheckCheck size={16} />
            Marcar todas como leídas
          </button>
        )}
      </div>

      {/* Empty state */}
      {items.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Bell size={48} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No tienes notificaciones</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => open(n)}
              className={`
                w-full text-left p-4 rounded-xl border flex items-start gap-3 group transition
                ${n.read
                  ? "bg-zinc-900 border-zinc-800 hover:bg-zinc-800"
                  : "bg-amber-950/30 border-amber-800/50 hover:bg-amber-950/50"}
              `}
            >
              {/* Unread dot */}
              <span className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${n.read ? "" : "bg-supernova-500"}`} />

              <div className="flex-1 min-w-0">
                <p className={`font-semibold text-sm ${n.read ? "text-gray-200" : "text-white"}`}>
                  {n.title}
                </p>
                <p className="text-sm text-gray-400 mt-0.5">{n.message}</p>
                <p className="text-xs text-gray-400 mt-1">{dayjs(n.createdAt).locale("es").fromNow()}</p>
              </div>

              <button
                onClick={(e) => deleteNotif(e, n.id)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-red-900/30 text-red-400 transition flex-shrink-0"
                title="Eliminar"
              >
                <Trash2 size={15} />
              </button>
            </button>
          ))}
        </div>
      )}
      <MobileBottomBar />
    </div>
  );
}
